import { FontStyles } from "../utils/font-styles.ts";

export type SingleNumericKeyInfoType =
  | "bedrooms"
  | "bathrooms"
  | "reception-rooms"
  | "deposit"
  | "shared-ownership"
  | "ground-rent"
  | "service-charge"
  | "lease-length";
export type TextKeyInfoType = "epc-rating" | "council-tax-band" | "tenure";
export type KeyInfoType = SingleNumericKeyInfoType | TextKeyInfoType | "area";

interface KeyInfoProps {
  text: string;
  className?: string;
}

const Icons: Record<KeyInfoType, string> = {
  area: "fa-ruler",
  bathrooms: "fa-bath",
  bedrooms: "fa-bed",
  "council-tax-band": "fa-house-chimney",
  deposit: "fa-key",
  "epc-rating": "fa-bars-sort fa-flip-vertical",
  "reception-rooms": "fa-couch",
  tenure: "fa-key",
  "shared-ownership": "fa-percent",
  "ground-rent": "fa-signs-post",
  "service-charge": "fa-user-vneck",
  "lease-length": "fa-calendar",
};
const KeyInfo = (props: KeyInfoProps) => {
  return (
    <div className={"flex gap-2 text-nowrap"}>
      <span
        className={`!flex items-center fa-solid fa-sharp text-loch-400 ${props.className ?? ""} fa-fw h-[20px]`}
        aria-hidden="true"
      ></span>
      <span className={`${FontStyles["body-md"]} text-north-sea font-semibold`}>{props.text}</span>
    </div>
  );
};

export const AreaKeyInfo = ({ metresSquared }: { metresSquared: number }) => {
  const squareFootage = Math.round(metresSquared * 10.7639);
  return <KeyInfo text={`${squareFootage.toString()}ft² / ${metresSquared.toString()}m²`} className={Icons.area} />;
};

export const BathroomsKeyInfo = ({ bathrooms }: { bathrooms: number }) => {
  const bathroomsText = bathrooms === 1 ? "bathroom" : "bathrooms";
  return <KeyInfo text={`${bathrooms.toString()} ${bathroomsText}`} className={Icons.bathrooms} />;
};

export const BedroomsKeyInfo = ({ bedrooms }: { bedrooms: number }) => {
  const bedroomsText = bedrooms === 1 ? "bedroom" : "bedrooms";
  return <KeyInfo text={`${bedrooms.toString()} ${bedroomsText}`} className={Icons.bedrooms} />;
};

export const CouncilTaxBandKeyInfo = ({ band }: { band: string }) => {
  return <KeyInfo text={`Council tax band ${band}`} className={Icons["council-tax-band"]} />;
};

export const DepositKeyInfo = ({ deposit }: { deposit: number }) => {
  return <KeyInfo text={`£${deposit.toLocaleString("en-GB")} deposit`} className={Icons.deposit} />;
};

export const EpcRatingKeyInfo = ({ rating }: { rating: string }) => {
  return <KeyInfo text={`EPC rating ${rating}`} className={Icons["epc-rating"]} />;
};

export const ReceptionRoomsKeyInfo = ({ receptionRooms }: { receptionRooms: number }) => {
  const roomsText = receptionRooms === 1 ? "reception room" : "reception rooms";
  return <KeyInfo text={`${receptionRooms.toString()} ${roomsText}`} className={Icons["reception-rooms"]} />;
};

export const TenureKeyInfo = ({ tenure }: { tenure: string }) => {
  return <KeyInfo text={tenure} className={Icons.tenure} />;
};

export const SharedOwnershipKeyInfo = ({ sharedOwnership }: { sharedOwnership: number }) => {
  return <KeyInfo text={`${sharedOwnership.toString()}% shared ownership`} className={Icons["shared-ownership"]} />;
};

export const GroundRentKeyInfo = ({ groundRent }: { groundRent: number }) => {
  return <KeyInfo text={`£${groundRent.toLocaleString("en-GB")} ground rent`} className={Icons["ground-rent"]} />;
};

export const ServiceChargeKeyInfo = ({ serviceCharge }: { serviceCharge: number }) => {
  return (
    <KeyInfo text={`£${serviceCharge.toLocaleString("en-GB")} service charge`} className={Icons["service-charge"]} />
  );
};

export const LeaseLengthKeyInfo = ({ leaseLength, rental }: { leaseLength: number; rental: boolean }) => {
  return (
    <KeyInfo
      text={`${leaseLength.toString()} ${
        rental ? (leaseLength == 1 ? "month" : "months") : leaseLength == 1 ? "year" : "years"
      }`}
      className={Icons["lease-length"]}
    />
  );
};
